import React from 'react'
import { Columns, Element, Icon } from 'react-bulma-components'
import { User, UserAssociation } from '../../api/users'
import Modal, { useModal } from '../modal'
import ResourceActions from '../resources/resource-actions'
import useStore from '../../store'
import {
  getWorkerAndOrganizationAssociation,
  getActiveContractualSchedule,
  getWorkerEmployerRelationData,
} from '../../utils/users'
import { CheckIcon } from '../icons'
import { BanIcon } from '../icons'
import ShowQrCodeButton from '../users/buttons/show-qr-code'
import useQrCode from '../../hooks/use-qr-code'
import RoleStatus from '../users/role-status'
import DiscontinueAssociationButton from '../users/buttons/discontinue-association'
import { Organization } from '../../api/organizations'
import RoleStatusDescription from '../users/role-status-description'
import EditUserButton from '../users/buttons/edit-user'
import ActiveEmployerCollaborationButton from '../users/buttons/active-employer-collaboration'
import Protected from '../protected/protected'
import { useWorkerCountersQuery } from '../../queries/worker-counters'
import { dateInputToDateValue, dateToDateInputValue, formatDuration } from '../../utils/date'
import WorkerCounterModal from '../worker-counters/worker-counter-modal'
import { WorkerCounter } from '../../api/worker-counters'
import { useUpdateWorkerAutoClockingMutation } from '../../queries/users'
import AutoClockingAction from './auto-clocking-action'
import AutoClockingPreview from './auto-clocking-preview'

interface WorkerActionsProps {
  worker: User
  isEmployerCollaborationInactive?: boolean
  isExternalWorker?: boolean
  association?: UserAssociation
}

/**
 * Component to display actions available for a worker
 */
const WorkerActions: React.FC<WorkerActionsProps> = ({
  worker,
  isEmployerCollaborationInactive,
  isExternalWorker,
  association,
}) => {
  const actions = useActions(worker, isEmployerCollaborationInactive, isExternalWorker, association)

  return <ResourceActions actions={actions} />
}

export default WorkerActions

interface Action {
  title: React.ReactNode
  description?: string
  content: React.ReactNode
}

const useActions = (
  worker: User,
  isEmployerCollaborationInactive?: boolean,
  isExternalWorker?: boolean,
  association?: UserAssociation,
): Action[] => {
  const workerCounterModal = useModal()
  const autoClockingModal = useModal()
  const currentOrganization = useStore(state => state.session.currentOrganization)
  const currentUserRole = useStore(state => state.session.currentUserRole)
  const workerCounters = useWorkerCountersQuery(
    { worker: worker._id },
    { enabled: Boolean(worker._id) },
  )
  const updateWorkerAutoClockingMutation = useUpdateWorkerAutoClockingMutation()
  const qrCode = useQrCode(worker._id, isExternalWorker)
  const workerAssociation =
    currentOrganization && getWorkerAndOrganizationAssociation(worker, currentOrganization)
  const isActive = workerAssociation?.status === 'accepted'
  const workerEmployerRelation =
    currentOrganization && getWorkerEmployerRelationData(worker, currentOrganization)
  const canManageAutoClocking =
    currentUserRole === 'employerMember' && currentOrganization?.type === 'employer'

  // State pour gérer la modale des compteurs
  const [selectedWorkerCounter, setSelectedWorkerCounter] = React.useState<WorkerCounter | null>(
    null,
  )

  // Réinitialiser le worker counter sélectionné quand la modale se ferme
  React.useEffect(() => {
    if (!workerCounterModal.isDisplayed) {
      setSelectedWorkerCounter(null)
    }
  }, [workerCounterModal.isDisplayed])

  return React.useMemo(
    () =>
      [
        isExternalWorker && {
          title: (
            <Icon.Text>
              <Icon.Text>Collaboration avec l'intérimaire</Icon.Text>
              <Icon color={isEmployerCollaborationInactive ? 'danger' : 'success'}>
                {isEmployerCollaborationInactive ? <BanIcon /> : <CheckIcon />}
              </Icon>
            </Icon.Text>
          ),
          content: (
            <>
              {isEmployerCollaborationInactive ? (
                <>
                  <Element renderAs="p" mb={3}>
                    Vous avez signalé aux agences que vous ne souhaitez plus travailler avec cet
                    intérimaire.
                  </Element>
                  <ActiveEmployerCollaborationButton user={worker} isInactive={true} />
                </>
              ) : (
                <>
                  <Element renderAs="p" mb={3}>
                    Si vous ne souhaitez plus travailler avec cet intérimaire, vous pouvez le
                    signaler aux agences ici.
                    <br /> Vous pourrez le réintégrer à tout moment.
                  </Element>
                  <ActiveEmployerCollaborationButton user={worker} isInactive={false} />
                </>
              )}
            </>
          ),
        },
        !isExternalWorker &&
          association && {
            title: 'Informations',
            content: (
              <>
                <Element renderAs="p" mb={3}>
                  <Columns mt={3}>
                    <Protected roles={['employerMember']}>
                      {(() => {
                        const activeSchedule = getActiveContractualSchedule(association)
                        return (
                          <>
                            <Columns.Column size={3} py={1}>
                              <Element renderAs="strong">Heures mensuelles: </Element>
                              {activeSchedule?.monthlyHoursWorked ?? 'N/A'} h/mois
                            </Columns.Column>
                            <Columns.Column size={3} py={1}>
                              <Element renderAs="strong">Heures hebdomadaires: </Element>
                              {activeSchedule?.weeklyHoursWorked ?? 'N/A'} h/sem
                            </Columns.Column>
                          </>
                        )
                      })()}
                    </Protected>
                    <Columns.Column size={12} py={1}>
                      <Element renderAs="strong">Informations supplémentaires: </Element>
                      {association.internalComment || 'N/A'}
                    </Columns.Column>
                    <Columns.Column size={12} py={1}>
                      <Element renderAs="strong">Informations interne: </Element>
                      {association.internalInformation || 'N/A'}
                    </Columns.Column>
                  </Columns>
                  <EditUserButton
                    user={worker}
                    userRoleKind="worker"
                    label="Modifier"
                    color={'primary'}
                  />
                </Element>
              </>
            ),
          },
        canManageAutoClocking && {
          title: 'Auto-pointage',
          content: (
            <>
              <Element renderAs="p" mb={3}>
                <AutoClockingPreview autoClocking={workerEmployerRelation?.autoClocking} />
              </Element>
              <Element>
                <Element
                  renderAs="button"
                  className="button is-primary is-small"
                  onClick={() => autoClockingModal.setIsDisplayed(true)}
                  data-test="edit-auto-clocking"
                >
                  Modifier
                </Element>
              </Element>
              <Modal
                title="Configurer l'auto-pointage"
                actions={autoClockingModal}
                maxWidth={700}
                dataTest="auto-clocking-modal"
              >
                <AutoClockingAction
                  enabledDefault={Boolean(workerEmployerRelation?.autoClocking?.enabled)}
                  fromDefault={
                    workerEmployerRelation?.autoClocking?.from
                      ? dateToDateInputValue(new Date(workerEmployerRelation.autoClocking.from))
                      : ''
                  }
                  toDefault={
                    workerEmployerRelation?.autoClocking?.to
                      ? dateToDateInputValue(new Date(workerEmployerRelation.autoClocking.to))
                      : ''
                  }
                  onSubmit={async values => {
                    await updateWorkerAutoClockingMutation.mutateAsync({
                      workerId: worker._id,
                      enabled: values.enabled,
                      from:
                        values.enabled && values.from
                          ? dateInputToDateValue(values.from)
                          : undefined,
                      to: values.enabled && values.to ? dateInputToDateValue(values.to) : undefined,
                    })
                    autoClockingModal.setIsDisplayed(false)
                  }}
                  mutation={updateWorkerAutoClockingMutation}
                />
              </Modal>
            </>
          ),
        },
        workerCounters.data?.[0] &&
          !isExternalWorker && {
            title: 'Compteurs',
            content: (
              <>
                <Element renderAs="p" mb={3}>
                  {workerCounters.data?.map(counter => (
                    <Element key={counter._id} mb={2}>
                      <Element renderAs="strong">{counter.template.name}: </Element>
                      <Element
                        renderAs="span"
                        style={{ cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => {
                          setSelectedWorkerCounter(counter)
                          workerCounterModal.setIsDisplayed(true)
                        }}
                      >
                        {formatDuration(counter.currentValue)}
                      </Element>
                    </Element>
                  ))}
                </Element>

                {/* Modal pour les détails du worker counter */}
                <WorkerCounterModal
                  workerCounter={selectedWorkerCounter || undefined}
                  isDisplayed={workerCounterModal.isDisplayed}
                  setIsDisplayed={workerCounterModal.setIsDisplayed}
                />
              </>
            ),
          },
        {
          title: 'Impression QR code',
          content: (
            <>
              <Element renderAs="p" mb={3}>
                {isExternalWorker && !qrCode ? (
                  <>
                    Pour les intérimaires, le QR code n'est disponible que pendant une mission
                    active ou dans les 3 jours précédant son début.
                  </>
                ) : (
                  <>
                    Vous pouvez imprimer le QR code du travailleur afin qu'il puisse pointer sans
                    son smartphone. <br />
                    Cependant, il est préférable pour des raisons de sécurité et de suivi que les
                    travailleurs utilisent l'application mobile.
                  </>
                )}
              </Element>
              {qrCode && (
                <ShowQrCodeButton
                  user={worker}
                  color="primary"
                  label="Afficher le QR code"
                  canRegenerate={false}
                  isExternalWorker={isExternalWorker}
                />
              )}
            </>
          ),
        },
        !isExternalWorker &&
          association && {
            title: (
              <Icon.Text>
                Collaboration
                <RoleStatus role={association} />
              </Icon.Text>
            ),
            content: (
              <>
                <Element renderAs="p" mb={3}>
                  <RoleStatusDescription role={association} />
                </Element>
                {(association.status === 'accepted' || association.status === 'pending') && (
                  <DiscontinueAssociationButton
                    user={worker}
                    color="danger"
                    roleKind="worker"
                    organization={currentOrganization as Organization}
                  />
                )}
              </>
            ),
          },
      ].filter(v => v) as Action[],
    [
      worker,
      workerCounterModal,
      autoClockingModal,
      isActive,
      isExternalWorker,
      canManageAutoClocking,
      workerEmployerRelation?.autoClocking?.enabled,
      workerEmployerRelation?.autoClocking?.from,
      workerEmployerRelation?.autoClocking?.to,
      updateWorkerAutoClockingMutation,
      isEmployerCollaborationInactive,
      qrCode,
      association,
    ],
  )
}
